// Centralized JSON-LD schema builders for SEO authority signals.
// Imported by SEO component and pages.

const SITE = (
  import.meta.env["VITE_SITE_URL"] || "https://imports.com.gh"
).replace(/\/$/, "");

// Only facts that already appear on the live site (name, URL, phone, Twitter handle
// from index.html). Do NOT add logo/address/socials/founding details here unless
// they are real and verifiable; invented entity data is worse than none.
export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE}/#organization`,
  name: "Imports.com.gh",
  url: SITE,
  description:
    "Source, quote and ship vehicles and goods to Ghana with transparent GHS pricing.",
  areaServed: { "@type": "Country", name: "Ghana" },
  telephone: "+233548233950",
  sameAs: ["https://twitter.com/imports_ghana"],
} as const;

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE}/#website`,
  url: SITE,
  name: "Imports.com.gh",
  publisher: { "@id": `${SITE}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE}/vehicles?make={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export function breadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : `${SITE}${item.url}`,
    })),
  };
}

export interface ArticleSchemaInput {
  headline: string;
  description: string;
  slug: string;
  authorName: string;
  authorUrl?: string | undefined;
  datePublished: string;
  dateModified?: string | undefined;
  image?: string | undefined;
  category: string;
  wordCount: number;
}

export function articleSchema(a: ArticleSchemaInput) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: a.headline,
    description: a.description,
    image: a.image ? [a.image] : undefined,
    datePublished: a.datePublished,
    dateModified: a.dateModified ?? a.datePublished,
    wordCount: a.wordCount,
    articleSection: a.category,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE}/insights/${a.slug}`,
    },
    author: {
      "@type": "Person",
      name: a.authorName,
      ...(a.authorUrl ? { url: a.authorUrl } : {}),
    },
    publisher: {
      "@id": `${SITE}/#organization`,
    },
  };
}

export interface FaqSchemaInput {
  question: string;
  answer: string;
}

export function faqSchema(faqs: FaqSchemaInput[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

export interface HowToStep {
  name: string;
  text: string;
}

export function howToSchema(opts: {
  name: string;
  description: string;
  totalTimeIso?: string;
  estimatedCost?: { currency: string; value: string };
  steps: HowToStep[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: opts.name,
    description: opts.description,
    ...(opts.totalTimeIso ? { totalTime: opts.totalTimeIso } : {}),
    ...(opts.estimatedCost
      ? {
          estimatedCost: {
            "@type": "MonetaryAmount",
            currency: opts.estimatedCost.currency,
            value: opts.estimatedCost.value,
          },
        }
      : {}),
    step: opts.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}
